import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PenLine, Users, ShieldCheck, IndianRupee, X } from 'lucide-react';

// Informational — shown from the homepage's "Become an Author" CTA so a
// reader knows the royalty split and terms before heading to /publish.
// Purely dismissible; the actual Premium-membership gate lives on the
// /publish page itself (pages/Publish.jsx), not here.
export default function AuthorInfoModal({ open, onClose, onContinue, continueLabel = 'Continue to Publish', royaltyPercent = 70 }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm"
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl2 bg-white dark:bg-surface-darkCard p-6 shadow-soft-lg sm:p-8"
            >
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute right-4 top-4 rounded-full p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
              >
                <X size={18} />
              </button>

              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-primary text-white shadow-glow">
                <PenLine size={20} />
              </span>
              <h3 className="mt-4 font-display text-xl font-semibold text-slate-900 dark:text-white">
                Publish your book with us
              </h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Publishing is a Premium membership benefit. Upgrade to unlock manuscript submission,
                and you'll be set up with an author account right away.
              </p>

              <ul className="mt-4 space-y-2.5 text-sm text-slate-600 dark:text-slate-300">
                <li className="flex items-start gap-2.5">
                  <IndianRupee size={16} className="mt-0.5 shrink-0 text-primary-500" />
                  Keep {royaltyPercent}% royalty on every sale
                </li>
                <li className="flex items-start gap-2.5">
                  <Users size={16} className="mt-0.5 shrink-0 text-primary-500" />
                  Reach thousands of active readers
                </li>
                <li className="flex items-start gap-2.5">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0 text-primary-500" />
                  Get paid directly to your bank account
                </li>
              </ul>

              <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                By continuing, you agree to our{' '}
                <Link to="/legal/terms" target="_blank" className="underline hover:text-primary-500" onClick={onClose}>
                  Author &amp; Royalty Terms
                </Link>.
              </p>

              <div className="mt-6 flex justify-end gap-2">
                <button onClick={onClose} className="btn-secondary !px-4 py-2 text-sm">
                  Maybe later
                </button>
                <button onClick={onContinue} className="btn-primary !px-4 py-2 text-sm">
                  {continueLabel}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
